"use client";

import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import PersonalInfoForm from "./PersonalInfoForm";
import CommissionInfoForm from "./CommissionInfoForm";
import StepHeader from "./StepHeader";

interface FormData {
  name: string;
  surname: string;
  phone: string;
  email: string;
  businessName: string;
  organisationCategory: string;
  agreeTerms: boolean;
  paymentMethod: string;
  bankName: string;
  bankNumber: string;
  accountHolderName: string;
}

interface FormErrors {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  businessName?: string;
  organisationCategory?: string;
  agreeTerms?: string;
  paymentMethod?: string;
}

interface RegistrationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ReferralProgramPayload {
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  consultant_name: string;
  organisation_category: string;
  payment_method: string;
  bank_name: string;
  bank_number: string;
  account_holder_name: string;
}

interface ReferralProgramResponse {
  id: number | string;
  name: string;
}

export default function RegistrationForm({
  open,
  onClose,
  onSuccess,
}: RegistrationFormProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    surname: "",
    phone: "+263",
    email: "",
    businessName: "",
    organisationCategory: "",
    agreeTerms: false,
    paymentMethod: "",
    bankName: "",
    bankNumber: "",
    accountHolderName: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    if (field === "paymentMethod") {
      setFormData((prev) => ({
        ...prev,
        paymentMethod: value as string,
        bankName: "",
        bankNumber: "",
        accountHolderName: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      surname: "",
      phone: "+263",
      email: "",
      businessName: "",
      organisationCategory: "",
      agreeTerms: false,
      paymentMethod: "",
      bankName: "",
      bankNumber: "",
      accountHolderName: "",
    });
    setErrors({});
    setStep(1);
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.surname.trim()) newErrors.surname = "Surname is required";
    if (!formData.phone.trim() || formData.phone === "+263")
      newErrors.phone = "Phone number is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }
    if (!formData.businessName.trim())
      newErrors.businessName = "Business name is required";
    if (!formData.organisationCategory)
      newErrors.organisationCategory = "Select a category";
    if (!formData.agreeTerms)
      newErrors.agreeTerms = "You must agree to the terms";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.paymentMethod)
      newErrors.paymentMethod = "Select a payment method";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validateStep2()) return;

    setIsSubmitting(true);

    const payload: ReferralProgramPayload = {
      name: formData.name.trim(),
      surname: formData.surname.trim(),
      email: formData.email.trim(),
      phone_number: formData.phone.trim(),
      consultant_name: formData.businessName.trim(),
      organisation_category: formData.organisationCategory,
      payment_method: formData.paymentMethod,
      bank_name: formData.bankName.trim(),
      bank_number: formData.bankNumber.trim(),
      account_holder_name: formData.accountHolderName.trim(),
    };

    try {
      await apiService.post<ReferralProgramResponse>(
        "/api/refferal-program/refferal-programs/",
        payload,
      );
      toast.success("Registration submitted successfully");
      resetForm();
      onSuccess();
    } catch (err) {
      console.error("Failed to submit referral registration", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 5, overflow: "hidden", maxHeight: "90vh" },
      }}
    >
      {/* close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "#fff",
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader step={step} />

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {step === 1 ? (
          <PersonalInfoForm
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onNext={handleNext}
          />
        ) : (
          <CommissionInfoForm
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
